import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RichTextEditor } from "@/admin/RichTextEditor";
import { ImageUpload } from "@/admin/ImageUpload";

const testid = (name) => `field-${name}`;

const StringList = ({ value, onChange }) => {
  const arr = Array.isArray(value) ? value : [];
  const set = (i, v) => onChange(arr.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="space-y-2">
      {arr.map((item, i) => (
        <div key={i} className="flex gap-2">
          <Input value={item} onChange={(e) => set(i, e.target.value)} className="rounded-sm" />
          <Button type="button" variant="outline" size="icon" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="rounded-sm">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...arr, ""])} className="rounded-sm">
        <Plus className="mr-1 h-4 w-4" /> Add
      </Button>
    </div>
  );
};

const ObjectList = ({ value, onChange, subfields }) => {
  const arr = Array.isArray(value) ? value : [];
  const setField = (i, key, v) => onChange(arr.map((row, idx) => (idx === i ? { ...row, [key]: v } : row)));
  const emptyRow = subfields.reduce((a, f) => ({ ...a, [f.name]: "" }), {});
  return (
    <div className="space-y-3">
      {arr.map((row, i) => (
        <div key={i} className="rounded-sm border border-input p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {subfields.map((f) => (
              f.type === "image" ? (
                <div key={f.name} className="sm:col-span-2">
                  <Label className="text-xs">{f.label}</Label>
                  <ImageUpload value={row[f.name]} onChange={(v) => setField(i, f.name, v)} />
                </div>
              ) : (
                <div key={f.name}>
                  <Label className="text-xs">{f.label}</Label>
                  <Input value={row[f.name] || ""} onChange={(e) => setField(i, f.name, e.target.value)} className="rounded-sm" />
                </div>
              )
            ))}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(arr.filter((_, idx) => idx !== i))} className="mt-2 text-destructive">
            <Trash2 className="mr-1 h-4 w-4" /> Remove
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...arr, { ...emptyRow }])} className="rounded-sm">
        <Plus className="mr-1 h-4 w-4" /> Add
      </Button>
    </div>
  );
};

const ObjectFields = ({ value, onChange, subfields }) => {
  const obj = value && typeof value === "object" ? value : {};
  return (
    <div className="grid gap-3 rounded-sm border border-input p-3 sm:grid-cols-2">
      {subfields.map((f) => (
        <div key={f.name}>
          <Label className="text-xs">{f.label}</Label>
          <Input value={obj[f.name] || ""} onChange={(e) => onChange({ ...obj, [f.name]: e.target.value })} className="rounded-sm" />
        </div>
      ))}
    </div>
  );
};

export const Field = ({ field, value, onChange }) => {
  const { name, label, type, options, subfields, help, required } = field;
  const full = ["textarea", "richtext", "image", "objectlist", "object", "list"].includes(type);

  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label htmlFor={name} className="flex items-center gap-1">
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <div className="mt-2">
        {type === "text" && <Input id={name} data-testid={testid(name)} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" />}
        {type === "number" && <Input id={name} type="number" data-testid={testid(name)} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="rounded-sm" />}
        {type === "date" && <Input id={name} type="date" data-testid={testid(name)} value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-sm" />}
        {type === "textarea" && <Textarea id={name} data-testid={testid(name)} value={value || ""} onChange={(e) => onChange(e.target.value)} className="min-h-[110px] rounded-sm" />}
        {type === "richtext" && <RichTextEditor value={value} onChange={onChange} testid={testid(name)} />}
        {type === "image" && <ImageUpload value={value} onChange={onChange} testid={testid(name)} />}
        {type === "switch" && <Switch data-testid={testid(name)} checked={!!value} onCheckedChange={onChange} />}
        {type === "tags" && (
          <Input
            data-testid={testid(name)}
            value={Array.isArray(value) ? value.join(", ") : (value || "")}
            onChange={(e) => onChange(e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
            placeholder="comma, separated, tags"
            className="rounded-sm"
          />
        )}
        {type === "select" && (
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger data-testid={testid(name)} className="rounded-sm"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {type === "list" && <StringList value={value} onChange={onChange} />}
        {type === "objectlist" && <ObjectList value={value} onChange={onChange} subfields={subfields} />}
        {type === "object" && <ObjectFields value={value} onChange={onChange} subfields={subfields} />}
      </div>
      {help && <p className="mt-1 text-xs text-muted-foreground">{help}</p>}
    </div>
  );
};

export const DynamicForm = ({ fields, values, onChange }) => (
  <div className="grid gap-5 sm:grid-cols-2">
    {fields.map((f) => (
      <Field key={f.name} field={f} value={values[f.name]} onChange={(v) => onChange(f.name, v)} />
    ))}
  </div>
);

export default DynamicForm;
